/**
 * 67.70: the calls' marks on the ground (ui/callMarks.ts gives the shapes) — draped over the drawn ground, never a flat
 * disc sinking into a slope: a circle is a band of quads round its edge (a dashed one every other quad) over a faint fan; a
 * landing box its four edges and a faint floor, a chevron at its front. The aimed call's mark is built when its shape
 * changes (the host asks only when the point moves); a standing call's once, with a column over it to find it from afar
 * and its countdown in a label above. Reads the state through the shapes; writes nothing.
 *
 * Cost: building a mark is its band's ground probes (a circle 32–128 quads, a box ~60), only when the aimed point moves or
 * a call is made; each frame a label's projection per mark.
 */
import * as THREE from 'three';
import type { PlanetState, Vec3 } from '../types';
import type { CallShape, StandingCall } from '../ui/callMarks';

type Ground = (planet: PlanetState, dir: Vec3) => number;

/** The marks' look: their band (people, and × the circle's radius), the lift over the ground, the fills' and the column's opacity. */
export const CALL_MARK_LOOK = { band: 0.3, bandShare: 0.04, lift: 0.12, fill: 0.07, edge: 0.75, dash: 1.4, column: 8, refused: '#ff5a4a', strike: '#ffb04a', belowMenu: 140 };

interface Built { group: THREE.Group; label: HTMLDivElement; anchor: THREE.Vector3; up: THREE.Vector3; text: string }

/** A unit tangent at `n` and its partner. */
function frame(n: Vec3): { a: Vec3; b: Vec3 } {
  const ref = Math.abs(n.y) < 0.9 ? { x: 0, y: 1, z: 0 } : { x: 1, y: 0, z: 0 };
  const ax = n.y * ref.z - n.z * ref.y, ay = n.z * ref.x - n.x * ref.z, az = n.x * ref.y - n.y * ref.x, al = Math.hypot(ax, ay, az);
  const a = { x: ax / al, y: ay / al, z: az / al };
  return { a, b: { x: n.y * a.z - n.z * a.y, y: n.z * a.x - n.x * a.z, z: n.x * a.y - n.y * a.x } };
}

export class CallMarksView {
  private aimed: { key: string; built: Built } | null = null;
  private readonly standing = new Map<string, Built>();
  private readonly box: HTMLDivElement | null;

  constructor(private readonly scene: THREE.Scene, private readonly ground: Ground) {
    this.box = typeof document !== 'undefined' ? document.body.appendChild(Object.assign(document.createElement('div'), { style: 'position:fixed; left:0; top:0; pointer-events:none; z-index:12;' })) : null;
  }

  /**
   * One frame: the aimed call (null: none aimed), the side's standing calls, the planets, a person's world size, the camera,
   * each call's colour; `below` (px): the aimed label is put this far under its point (a menu open over it).
   */
  update(aimed: CallShape | null, standing: readonly StandingCall[], planets: readonly PlanetState[], person: number, camera: THREE.Camera, colour: (s: CallShape) => string, below = 0): void {
    const aimKey = aimed ? `${aimed.sub}|${aimed.label}|${aimed.circles.map(c => `${c.at.x.toFixed(6)},${c.at.y.toFixed(6)},${c.at.z.toFixed(6)},${c.people}`).join(';')}|${aimed.box ? `${aimed.box.at.x.toFixed(6)},${aimed.box.at.z.toFixed(6)}` : ''}` : '';
    if ((this.aimed?.key ?? '') !== aimKey) {
      if (this.aimed) this.drop(this.aimed.built);
      this.aimed = null;
      const planet = aimed ? planets[aimed.planetIndex] : undefined;
      if (aimed && planet) this.aimed = { key: aimKey, built: this.build(aimed, planet, person, aimed.refused ? CALL_MARK_LOOK.refused : colour(aimed), false) };
    }
    if (this.aimed) this.place(this.aimed.built, this.aimed.built.text, camera, below);
    const seen = new Set<string>();
    for (const s of standing) {
      seen.add(s.key);
      let b = this.standing.get(s.key);
      const planet = planets[s.planetIndex];
      if (!b && planet) { b = this.build(s, planet, person, colour(s), true); this.standing.set(s.key, b); }
      if (b) this.place(b, `${s.label} · ${Math.ceil(s.seconds)} s`, camera);
    }
    for (const [k, b] of this.standing) if (!seen.has(k)) { this.drop(b); this.standing.delete(k); }
  }

  /** Marks drawn now (the probe's and the test's count). */
  get count(): number { return this.standing.size + (this.aimed ? 1 : 0); }

  dispose(): void {
    if (this.aimed) this.drop(this.aimed.built);
    for (const b of this.standing.values()) this.drop(b);
    this.aimed = null; this.standing.clear();
    this.box?.remove();
  }

  private drop(b: Built): void {
    this.scene.remove(b.group);
    b.group.traverse(o => { const m = o as THREE.Mesh; if (m.geometry) m.geometry.dispose(); const mat = m.material as THREE.Material | undefined; mat?.dispose(); });
    b.label.remove();
  }

  /** The label over its anchor (hidden behind the camera). */
  private place(b: Built, text: string, camera: THREE.Camera, below = 0): void {
    if (text !== b.text) { b.text = text; b.label.textContent = text; }
    if (typeof window === 'undefined') return;
    const p = b.anchor.clone().project(camera);
    const on = p.z < 1 && Math.abs(p.x) < 1.2 && Math.abs(p.y) < 1.2;
    b.label.style.display = on ? '' : 'none';
    if (on) b.label.style.transform = `translate(${((p.x + 1) / 2) * window.innerWidth}px, ${((1 - p.y) / 2) * window.innerHeight + below}px) translate(-50%, ${below ? '0' : '-100%'})`;
  }

  private build(s: CallShape, planet: PlanetState, person: number, hex: string, column: boolean): Built {
    const L = CALL_MARK_LOOK, group = new THREE.Group(), color = new THREE.Color(hex);
    const P = planet.position;
    /** A ground point `e`, `f` people along the tangents at `n`. */
    const at = (n: Vec3, t: { a: Vec3; b: Vec3 }, e: number, f: number): THREE.Vector3 => {
      const k = person / planet.radius;
      const x = n.x + (t.a.x * e + t.b.x * f) * k, y = n.y + (t.a.y * e + t.b.y * f) * k, z = n.z + (t.a.z * e + t.b.z * f) * k, l = Math.hypot(x, y, z);
      const d = { x: x / l, y: y / l, z: z / l }, r = this.ground(planet, d) + L.lift * person;
      return new THREE.Vector3(P.x + d.x * r, P.y + d.y * r, P.z + d.z * r);
    };
    const mat = (opacity: number) => new THREE.MeshBasicMaterial({ color, transparent: true, opacity, depthWrite: false, side: THREE.DoubleSide, polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -4 });
    const mesh = (pos: number[], idx: number[], opacity: number) => {
      const g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
      g.setIndex(idx);
      const m = new THREE.Mesh(g, mat(opacity));
      m.renderOrder = 3;
      group.add(m);
    };
    /** A band along a closed path of `n` points (each given by its parameter), `w` people wide; `dash`: every other quad. */
    const band = (n: number, point: (i: number, off: number) => THREE.Vector3, w: number, dash: boolean, opacity: number) => {
      const pos: number[] = [], idx: number[] = [];
      for (let i = 0; i <= n; i++) { const o = point(i % n, -w / 2), u = point(i % n, w / 2); pos.push(o.x, o.y, o.z, u.x, u.y, u.z); }
      for (let i = 0; i < n; i++) { if (dash && i % 2) continue; const a = 2 * i; idx.push(a, a + 1, a + 2, a + 1, a + 3, a + 2); }
      mesh(pos, idx, opacity);
    };
    for (const c of s.circles) {
      const n = c.at, t = frame(n), w = Math.max(L.band, c.people * L.bandShare);
      const segs = c.style === 'dashed' ? 2 * Math.max(8, Math.round((Math.PI * c.people) / L.dash)) : Math.max(32, Math.min(128, Math.round(16 + c.people * 4)));
      const ring = (i: number, off: number) => { const th = (i / segs) * 2 * Math.PI, r = c.people + off; return at(n, t, Math.cos(th) * r, Math.sin(th) * r); };
      band(segs, ring, w, c.style === 'dashed', L.edge);
      if (c.style === 'fill') {
        // a faint fan: three rings in, so it follows the ground
        const rings = 3, pos: number[] = [], idx: number[] = [];
        const ctr = at(n, t, 0, 0); pos.push(ctr.x, ctr.y, ctr.z);
        for (let k = 1; k <= rings; k++) for (let i = 0; i < segs; i++) { const th = (i / segs) * 2 * Math.PI, r = (c.people * k) / rings, p = at(n, t, Math.cos(th) * r, Math.sin(th) * r); pos.push(p.x, p.y, p.z); }
        const v = (k: number, i: number) => 1 + (k - 1) * segs + (i % segs);
        for (let i = 0; i < segs; i++) idx.push(0, v(1, i), v(1, i + 1));
        for (let k = 1; k < rings; k++) for (let i = 0; i < segs; i++) idx.push(v(k, i), v(k + 1, i), v(k + 1, i + 1), v(k, i), v(k + 1, i + 1), v(k, i + 1));
        mesh(pos, idx, L.fill);
      }
    }
    if (s.box) {
      const b = s.box, n = b.at, f = b.facing, hl = b.length / 2, hw = b.width / 2;
      const side = { x: n.y * f.z - n.z * f.y, y: n.z * f.x - n.x * f.z, z: n.x * f.y - n.y * f.x };
      const t = { a: f, b: side };
      // the edge, a point every half person round the rectangle (along a = forward, b = across)
      const corners: Array<[number, number]> = [[hl, hw], [-hl, hw], [-hl, -hw], [hl, -hw]];
      const pts: Array<[number, number, number, number]> = [];   // (e, f, the edge's inward normal e, f)
      for (let k = 0; k < 4; k++) {
        const [e0, f0] = corners[k], [e1, f1] = corners[(k + 1) % 4], len = Math.hypot(e1 - e0, f1 - f0), m = Math.max(2, Math.ceil(len / 0.5));
        const ne = -(f1 - f0) / len, nf = (e1 - e0) / len;
        for (let i = 0; i < m; i++) pts.push([e0 + ((e1 - e0) * i) / m, f0 + ((f1 - f0) * i) / m, ne, nf]);
      }
      const w = L.band;
      band(pts.length, (i, off) => { const [e, ff, ne, nf] = pts[i]; return at(n, t, e - ne * off, ff - nf * off); }, w, false, L.edge);
      // a faint floor, a quad strip along its length, and a chevron at its front (the way it faces: its nose)
      const strips = Math.max(2, Math.ceil(b.length / 0.5)), pos: number[] = [], idx: number[] = [];
      for (let i = 0; i <= strips; i++) { const e = -hl + (b.length * i) / strips, a = at(n, t, e, -hw), c = at(n, t, e, hw); pos.push(a.x, a.y, a.z, c.x, c.y, c.z); }
      for (let i = 0; i < strips; i++) { const a = 2 * i; idx.push(a, a + 1, a + 2, a + 1, a + 3, a + 2); }
      mesh(pos, idx, L.fill * 1.6);
      const tip = at(n, t, hl + 1.2, 0), l = at(n, t, hl + 0.2, 0.7), r = at(n, t, hl + 0.2, -0.7), l2 = at(n, t, hl + 0.55, 0.7), r2 = at(n, t, hl + 0.55, -0.7), tip2 = at(n, t, hl + 1.55, 0);
      mesh([l.x, l.y, l.z, tip.x, tip.y, tip.z, l2.x, l2.y, l2.z, tip2.x, tip2.y, tip2.z, r.x, r.y, r.z, r2.x, r2.y, r2.z], [0, 1, 2, 2, 1, 3, 1, 4, 3, 3, 4, 5], L.edge);
    }
    const centre = s.box?.at ?? s.circles[0]?.at ?? { x: 0, y: 1, z: 0 };
    const t0 = frame(centre), foot = at(centre, t0, 0, 0), up = new THREE.Vector3(centre.x, centre.y, centre.z).normalize();
    if (column) {
      const h = CALL_MARK_LOOK.column * person, cyl = new THREE.Mesh(new THREE.CylinderGeometry(0.25 * person, 0.25 * person, h, 10, 1, true), new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.35, depthWrite: false, side: THREE.DoubleSide }));
      cyl.position.copy(foot).addScaledVector(up, h / 2);
      cyl.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), up);
      group.add(cyl);
    }
    this.scene.add(group);
    const label = document.createElement('div');
    label.style.cssText = `position:absolute; left:0; top:0; white-space:nowrap; font:600 11px system-ui, sans-serif; color:${hex}; text-shadow:0 0 3px #000, 0 0 2px #000; padding-bottom:4px; display:none;`;
    label.textContent = s.label;
    this.box?.appendChild(label);
    return { group, label, anchor: foot.clone().addScaledVector(up, (column ? CALL_MARK_LOOK.column : 2.5) * person), up, text: s.label };
  }
}
